import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import { Row, Col, Button, Skeleton, DatePicker, Form, Alert } from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import LoginChecked from "../Shared/LoginChecked";
import { NumberFormat } from "../Shared/Utility";
import { parseReportAmount } from "../../Pdf/PrintFormateNumber";
import { IdentificationIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import moment from "moment";

const ASSET_ITEMS = [
  { label: "Stock value (at cost)", key: "productBalance" },
  { label: "Customer receivable", key: "customerReceiveable" },
  { label: "Supplier receivable", key: "supplierReceiveable" },
  { label: "Bank balance", key: "bankBalance" },
  { label: "Cash in hand", key: "cashBalance" },
];

const LIABILITY_ITEMS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Capital", key: "capitalBalance" },
];

function FinancialSummary() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [loading, setLoading] = useState(false);
  const [finalData, setFinalData] = useState({});
  const [asOfDate, setAsOfDate] = useState(dayjs());

  useEffect(() => {
    document.title = "Financial Summary";
    setLoading(true);

    axios({
      method: "post",
      url: config.base_url + "Report/GetFinancialSummary",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        EndDate: dayjs(asOfDate).format("YYYY-MM-DD"),
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setFinalData(response.data);
        } else {
          setFinalData({});
        }
        setLoading(false);
      })
      .catch(function () {
        setFinalData({});
        setLoading(false);
      });
  }, [asOfDate, UserID, AccessKey]);

  const totals = useMemo(() => {
    const totalAssets =
      parseReportAmount(finalData.totalAssets) ||
      ASSET_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(finalData[item.key]),
        0
      );
    const totalLiabilities =
      parseReportAmount(finalData.totalLiabilities) ||
      LIABILITY_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(finalData[item.key]),
        0
      );
    const grossAmount =
      parseReportAmount(finalData.grossAmount) || totalAssets - totalLiabilities;
    return { totalAssets, totalLiabilities, grossAmount };
  }, [finalData]);

  const fmt = (val) => NumberFormat(parseReportAmount(val), 0);

  const asOfLabel = moment(asOfDate).format(config.date_format);
  const isProfit = totals.grossAmount >= 0;

  const disabledDate = (current) =>
    current && current > moment().endOf("day");

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content financial-summary-page">
          <div className="page-header">
            <h3 className="page-title">Financial Summary</h3>

            <div className="header-actions">
              <NavLink
                to={`/report-final/print-list?AsOfDate=${dayjs(asOfDate).format(
                  "YYYY-MM-DD"
                )}`}
                target="_blank"
              >
                <Button
                  type="dashed"
                  shape="round"
                  size="large"
                  icon={<PrinterOutlined />}
                >
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <div className="filters-wrap financial-summary-filters">
            <Form layout="inline">
              <Form.Item label="As of date">
                <DatePicker
                  value={asOfDate}
                  onChange={(date) => date && setAsOfDate(date)}
                  disabledDate={disabledDate}
                  allowClear={false}
                  size="large"
                />
              </Form.Item>
            </Form>
          </div>

          <Alert
            type="info"
            showIcon
            className="financial-summary-alert"
            message={`Balances as of ${asOfLabel}`}
            description="This report is a point-in-time snapshot of stock and account balances, not a period activity report."
          />

          {loading ? (
            <Row gutter={[16, 16]} className="financial-summary-grid" justify="center">
              <Col xs={24} md={12} lg={11}>
                <Skeleton active paragraph={{ rows: 5 }} />
              </Col>
              <Col xs={24} md={12} lg={11}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Col>
              <Col xs={24} lg={22}>
                <Skeleton active paragraph={{ rows: 1 }} />
              </Col>
            </Row>
          ) : (
            <div className="financial-summary-body">
              <Row gutter={[16, 16]} className="financial-summary-grid" justify="center">
                <Col xs={24} md={12} lg={11}>
                  <div className="fb-card financial-summary-card">
                    <div className="fb-card-header">
                      <div className="fb-card-title">
                        <span>
                          <IdentificationIcon />
                        </span>
                        <h4>Assets / Receivables</h4>
                      </div>
                    </div>
                    <div className="fb-card-body">
                      <ul className="profit-list financial-summary-list">
                        {ASSET_ITEMS.map((item) => (
                          <li key={item.key}>
                            <span className="fs-label">{item.label}</span>
                            <span className="fs-value">{fmt(finalData[item.key])}</span>
                          </li>
                        ))}
                        <li className="financial-summary-total">
                          <span className="fs-label">Total assets</span>
                          <span className="fs-value netprofit">
                            {fmt(totals.totalAssets)}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Col>

                <Col xs={24} md={12} lg={11}>
                  <div className="fb-card financial-summary-card">
                    <div className="fb-card-header">
                      <div className="fb-card-title">
                        <span>
                          <IdentificationIcon />
                        </span>
                        <h4>Liabilities / Payables</h4>
                      </div>
                    </div>
                    <div className="fb-card-body">
                      <ul className="profit-list financial-summary-list">
                        {LIABILITY_ITEMS.map((item) => (
                          <li key={item.key}>
                            <span className="fs-label">{item.label}</span>
                            <span className="fs-value">{fmt(finalData[item.key])}</span>
                          </li>
                        ))}
                        <li className="financial-summary-total">
                          <span className="fs-label">Total liabilities</span>
                          <span className="fs-value netprofit">
                            {fmt(totals.totalLiabilities)}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Col>

                <Col xs={24} lg={22}>
                  <div
                    className={`fb-card financial-summary-net ${
                      isProfit ? "is-profit" : "is-loss"
                    }`}
                  >
                    <div className="fb-card-body">
                      <ul className="profit-list financial-summary-list">
                        <li>
                          <span className="fs-label">
                            Net position (assets − liabilities)
                          </span>
                          <span
                            className={`fs-value ${isProfit ? "profit" : "loss"}`}
                          >
                            {fmt(totals.grossAmount)}
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default FinancialSummary;
